<template>
    <div
        class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
        <div class="flex items-center justify-between gap-3 px-4 py-3">
            <div class="flex items-center gap-3">
                <span
                    class="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                >
                    Tem sinal de fraude? · Parecer IA
                </span>

                <span
                    v-if="verdict"
                    class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold"
                    :class="verdictBadgeClass"
                >
                    {{ verdictLabel }}
                    <span
                        v-if="confidence !== null"
                        class="font-medium opacity-80"
                    >
                        {{ confidence }}%
                    </span>
                </span>

                <span
                    v-else-if="streaming"
                    class="inline-flex items-center gap-2 text-xs text-indigo-500 dark:text-indigo-400"
                >
                    <span
                        class="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
                    ></span>
                    investigando...
                </span>
            </div>

            <div class="flex items-center gap-3">
                <span
                    v-if="createdAt && !streaming"
                    class="text-[11px] text-gray-400 dark:text-gray-500"
                    :title="createdAt"
                >
                    {{ freshnessLabel }}
                </span>

                <button
                    v-if="!streaming && enabled"
                    type="button"
                    class="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50"
                    :disabled="loadingLatest"
                    @click="generate"
                >
                    {{ report ? "Reanalisar" : "Auditar com IA" }}
                </button>
            </div>
        </div>

        <div
            v-if="error"
            class="border-t border-gray-100 px-4 py-3 text-sm text-red-600 dark:border-gray-700 dark:text-red-400"
        >
            {{ error }}
        </div>

        <div
            v-if="!enabled && !loadingLatest"
            class="border-t border-gray-100 px-4 py-3 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400"
        >
            Parecer de IA desabilitado neste ambiente.
        </div>

        <!-- Chain of thought: aberto durante a análise, uma linha depois -->
        <div
            v-if="steps.length > 0"
            class="border-t border-gray-100 px-4 py-3 dark:border-gray-700"
        >
            <button
                v-if="!streaming"
                type="button"
                class="flex items-center gap-2 text-xs text-gray-500 transition-colors hover:text-indigo-500 focus:outline-none dark:text-gray-400 dark:hover:text-indigo-400"
                :aria-expanded="showSteps"
                @click="showSteps = !showSteps"
            >
                <svg
                    class="h-3 w-3 transition-transform"
                    :class="{ 'rotate-90': showSteps }"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 5l7 7-7 7"
                    ></path>
                </svg>
                {{ steps.length }} verificações executadas
            </button>

            <ol
                v-if="streaming || showSteps"
                class="flex flex-col gap-1.5"
                :class="{ 'mt-2': !streaming }"
            >
                <li
                    v-for="step in steps"
                    :key="step.id"
                    class="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300"
                >
                    <span
                        v-if="step.done"
                        class="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400"
                    >
                        <svg
                            class="h-2.5 w-2.5"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="3"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </span>
                    <span
                        v-else
                        class="h-3.5 w-3.5 animate-spin rounded-full border-2 border-indigo-400 border-t-transparent"
                    ></span>

                    <span>{{ step.label }}</span>
                    <span
                        v-if="step.detail"
                        class="rounded bg-slate-200/60 px-1 py-px font-mono text-[0.85em] text-slate-600 dark:bg-slate-700/60 dark:text-slate-300"
                    >
                        {{ step.detail }}
                    </span>
                </li>
            </ol>
        </div>

        <!-- Parecer: resumo e ação na cara; evidências e detalhes atrás de um clique -->
        <div
            v-if="primaryBlocks.length > 0"
            class="flex flex-col gap-2 border-t border-gray-100 px-4 py-3 dark:border-gray-700"
        >
            <template v-for="(block, index) in primaryBlocks" :key="index">
                <h4
                    v-if="block.type === 'heading'"
                    class="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 first:mt-0 dark:text-gray-400"
                >
                    {{ block.text }}
                </h4>

                <ul
                    v-else-if="block.type === 'list'"
                    class="flex flex-col gap-1.5"
                >
                    <li
                        v-for="(item, itemIndex) in block.items"
                        :key="itemIndex"
                        class="flex gap-2 text-sm leading-relaxed text-gray-700 dark:text-gray-200"
                    >
                        <span
                            class="mt-2 h-1 w-1 shrink-0 rounded-full bg-gray-400 dark:bg-gray-500"
                        ></span>
                        <highlighted-text :text="item" />
                    </li>
                </ul>

                <p
                    v-else
                    class="text-sm leading-relaxed text-gray-700 dark:text-gray-200"
                >
                    <highlighted-text :text="block.text" />
                </p>
            </template>

            <button
                v-if="extraBlocks.length > 0"
                type="button"
                class="flex items-center gap-2 self-start text-xs font-medium text-indigo-600 transition-colors hover:text-indigo-500 focus:outline-none dark:text-indigo-400"
                :aria-expanded="showDetails"
                @click="showDetails = !showDetails"
            >
                <svg
                    class="h-3 w-3 transition-transform"
                    :class="{ 'rotate-90': showDetails }"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 5l7 7-7 7"
                    ></path>
                </svg>
                {{ showDetails ? "Ocultar" : "Ver" }} evidências e detalhes
            </button>

            <template v-if="showDetails">
                <template
                    v-for="(block, index) in extraBlocks"
                    :key="`x-${index}`"
                >
                    <h4
                        v-if="block.type === 'heading'"
                        class="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                    >
                        {{ block.text }}
                    </h4>

                    <ul
                        v-else-if="block.type === 'list'"
                        class="flex flex-col gap-1.5"
                    >
                        <li
                            v-for="(item, itemIndex) in block.items"
                            :key="itemIndex"
                            class="flex gap-2 text-sm leading-relaxed text-gray-700 dark:text-gray-200"
                        >
                            <span
                                class="mt-2 h-1 w-1 shrink-0 rounded-full bg-gray-400 dark:bg-gray-500"
                            ></span>
                            <highlighted-text :text="item" />
                        </li>
                    </ul>

                    <p
                        v-else
                        class="text-sm leading-relaxed text-gray-700 dark:text-gray-200"
                    >
                        <highlighted-text :text="block.text" />
                    </p>
                </template>
            </template>
        </div>
    </div>
</template>

<script>
import HighlightedText from "../../../views/monitoring/HighlightedText.vue";

const TOOL_LABELS = {
    ProducerMonitoringSignalsTool:
        "Consultando alertas e incidentes do monitoramento",
    ProducerProfileTool: "Levantando perfil e histórico da conta",
    ProducerCardVelocityTool: "Analisando velocity de recusas por BIN",
    ProducerTransactionBreakdownTool: "Abrindo o breakdown de transações",
    ProducerRefundSpeedTool: "Medindo velocidade de reembolso",
    ProducerIdentityStatsTool: "Cruzando identidades e fingerprints",
    ProducerDailyTimelineTool: "Reconstruindo a timeline diária",
};

/* Mesmos chips do vocabulário visual do monitoring (labels.js). */
const VERDICTS = {
    SIM: {
        label: "SIM",
        class: "bg-rose-500/10 text-rose-600 ring-1 ring-inset ring-rose-500/25 dark:text-rose-400",
    },
    SUSPEITO: {
        label: "SUSPEITO",
        class: "bg-amber-400/10 text-amber-600 ring-1 ring-inset ring-amber-400/30 dark:text-amber-400",
    },
    NAO: {
        label: "NÃO",
        class: "bg-emerald-500/10 text-emerald-600 ring-1 ring-inset ring-emerald-500/25 dark:text-emerald-400",
    },
};

export default {
    name: "SellerAiAudit",

    components: { HighlightedText },

    props: {
        user: {
            type: Object,
            required: true,
        },
    },

    emits: ["verdict"],

    data() {
        return {
            enabled: true,
            loadingLatest: true,
            streaming: false,
            report: "",
            verdict: null,
            confidence: null,
            createdAt: null,
            fresh: false,
            steps: [],
            error: null,
            pollTimer: null,
            pollStartedAt: null,
            showSteps: false,
            showDetails: false,
        };
    },

    computed: {
        verdictLabel() {
            return VERDICTS[this.verdict]?.label ?? this.verdict;
        },

        verdictBadgeClass() {
            return (
                VERDICTS[this.verdict]?.class ??
                "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
            );
        },

        freshnessLabel() {
            if (!this.createdAt) {
                return "";
            }

            const label = new Date(this.createdAt).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });

            return this.fresh ? label : `${label} · desatualizado`;
        },

        reportBody() {
            return this.report
                .replace(/^\s*VEREDITO:.*$/im, "")
                .replace(/^\s*CONFIANCA:.*$/im, "")
                .replace(/^\s*CONFIANÇA:.*$/im, "")
                .trim();
        },

        /**
         * Parser leve do markdown do parecer (headings ## e bullets) para
         * renderizar cada linha com o HighlightedText do monitoring, em vez
         * de HTML via markdown-it.
         */
        reportBlocks() {
            const blocks = [];

            for (const rawLine of this.reportBody.split("\n")) {
                const line = rawLine
                    .replace(/\*\*(.+?)\*\*/g, "$1")
                    .replace(/__(.+?)__/g, "$1")
                    .trim();

                if (line === "") {
                    continue;
                }

                const heading = line.match(/^#{1,4}\s+(.*)$/);

                if (heading) {
                    blocks.push({ type: "heading", text: heading[1] });
                    continue;
                }

                const bullet = line.match(/^[-*]\s+(.*)$/);

                if (bullet) {
                    const last = blocks[blocks.length - 1];

                    if (last?.type === "list") {
                        last.items.push(bullet[1]);
                    } else {
                        blocks.push({ type: "list", items: [bullet[1]] });
                    }

                    continue;
                }

                blocks.push({ type: "paragraph", text: line });
            }

            return blocks;
        },

        /**
         * Resumo e ação recomendada ficam na cara; evidências e detalhes
         * técnicos vão para trás do toggle "Ver evidências e detalhes".
         */
        primaryBlocks() {
            return this.splitBlocks().primary;
        },

        extraBlocks() {
            return this.splitBlocks().extra;
        },
    },

    mounted() {
        this.fetchLatest();
    },

    beforeUnmount() {
        this.stopPolling();
    },

    methods: {
        userId() {
            return this.user?.id ?? this.$route.params.user_id;
        },

        async fetchLatest() {
            this.loadingLatest = true;

            try {
                const { data } = await axios.get(
                    `/api/user/${this.userId()}/audit/ai-review`,
                );

                this.enabled = data.enabled;

                if (data.review) {
                    this.report = data.review.report ?? "";
                    this.verdict = data.review.verdict;
                    this.confidence = data.review.confidence;
                    this.createdAt = data.review.created_at;
                    this.fresh = data.review.fresh;
                    this.steps = (data.review.tool_steps ?? []).map(
                        (step, index) => ({
                            id: `saved-${index}`,
                            label: TOOL_LABELS[step.tool] ?? step.tool,
                            detail: this.stepDetail(step.arguments),
                            done: true,
                        }),
                    );
                    this.emitVerdict();
                }

                if (data.running) {
                    this.resumeProgress();
                }
            } catch (error) {
                // Sem parecer salvo não é erro: o card só oferece o botão.
            } finally {
                this.loadingLatest = false;
            }
        },

        async generate() {
            this.streaming = true;
            this.error = null;
            this.report = "";
            this.verdict = null;
            this.confidence = null;
            this.createdAt = null;
            this.steps = [];

            try {
                await axios.post(
                    `/api/user/${this.userId()}/audit/ai-review/generate`,
                );

                this.startPolling();
            } catch (error) {
                this.streaming = false;
                this.error =
                    error.response?.data?.message ??
                    "Falha ao iniciar o parecer de IA.";
            }
        },

        resumeProgress() {
            this.streaming = true;
            this.report = "";
            this.verdict = null;
            this.confidence = null;
            this.steps = [];
            this.startPolling();
        },

        startPolling() {
            this.pollStartedAt = Date.now();
            this.stopPolling();
            this.pollTimer = setInterval(() => this.poll(), 3000);
            this.poll();
        },

        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },

        async poll() {
            if (Date.now() - this.pollStartedAt > 6 * 60 * 1000) {
                this.stopPolling();
                this.streaming = false;
                this.error = "A análise demorou demais. Tente novamente.";
                return;
            }

            try {
                const { data } = await axios.get(
                    `/api/user/${this.userId()}/audit/ai-review/progress`,
                );

                if (data.status === "idle") {
                    return;
                }

                this.applySnapshot(data);

                if (data.status === "done") {
                    this.stopPolling();
                    this.streaming = false;
                    await this.fetchLatest();
                } else if (data.status === "failed") {
                    this.stopPolling();
                    this.streaming = false;
                    this.error =
                        data.error ?? "Falha ao gerar o parecer de IA.";
                }
            } catch (error) {
                // Erro transitório de rede: o próximo tick tenta de novo.
            }
        },

        splitBlocks() {
            const primary = [];
            const extra = [];
            let target = primary;

            for (const block of this.reportBlocks) {
                if (block.type === "heading") {
                    const normalized = block.text
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "")
                        .toLowerCase();

                    target =
                        normalized.includes("resumo") ||
                        normalized.includes("acao recomendada")
                            ? primary
                            : extra;
                }

                target.push(block);
            }

            return { primary, extra };
        },

        applySnapshot(snapshot) {
            this.report = snapshot.text ?? "";
            this.steps = (snapshot.steps ?? []).map((step) => ({
                id: step.id,
                label: TOOL_LABELS[step.tool] ?? step.tool,
                detail: this.stepDetail(step.arguments),
                done: step.done,
            }));
            this.parseVerdictFromReport();
        },

        parseVerdictFromReport() {
            const verdictMatch = this.report.match(
                /VEREDITO:\s*(SIM|SUSPEITO|N[AÃ]O)/i,
            );

            if (verdictMatch) {
                this.verdict = verdictMatch[1].toUpperCase().replace("Ã", "A");
            }

            const confidenceMatch = this.report.match(
                /CONFIAN[CÇ]A:\s*(\d{1,3})/i,
            );

            if (confidenceMatch) {
                this.confidence = Math.min(
                    100,
                    parseInt(confidenceMatch[1], 10),
                );
            }
        },

        stepDetail(args) {
            if (!args || typeof args !== "object") {
                return null;
            }

            if (args.bin) {
                return `BIN ${args.bin}`;
            }

            if (args.window_days) {
                return `${args.window_days}d`;
            }

            return null;
        },

        emitVerdict() {
            this.$emit("verdict", {
                verdict: this.verdict,
                confidence: this.confidence,
                fresh: this.fresh,
            });
        },
    },
};
</script>
